import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CloseIcon from '@material-ui/icons/Close';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import WarningIcon from '@material-ui/icons/Warning';
import errorGuideData from '../../data/error-guide.json';

export type ErrorGuideEntry = {
  id: string;
  pattern: string;
  name: string;
  severity: string;
  frequency: string;
  description: string;
  descriptionExtra?: string;
  causes: string[];
  steps: string[];
  callout?: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    helpIcon: {
      padding: 4,
      marginLeft: 6,
      color: '#0277BD',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#01579B',
        backgroundColor: 'rgba(2, 119, 189, 0.08)',
      },
    },
    dialog: {
      '& .MuiDialog-paper': {
        borderRadius: 12,
        overflow: 'hidden',
      },
    },
    header: {
      background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 50%, #0277BD 100%)',
      padding: theme.spacing(3, 3.5),
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'linear-gradient(90deg, #4FC3F7, #81D4FA, #4FC3F7)',
      },
    },
    headerIcon: {
      color: 'rgba(255, 255, 255, 0.25)',
      fontSize: 64,
      position: 'absolute',
      right: 24,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    headerTitle: {
      color: '#fff',
      fontWeight: 700,
      fontSize: '1.15rem',
      letterSpacing: '0.2px',
      lineHeight: 1.35,
      position: 'relative',
      zIndex: 1,
      paddingRight: 80,
    },
    headerSubtitle: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      marginBottom: theme.spacing(0.75),
      position: 'relative',
      zIndex: 1,
    },
    closeButton: {
      position: 'absolute',
      right: 8,
      top: 8,
      color: 'rgba(255, 255, 255, 0.6)',
      zIndex: 2,
      '&:hover': {
        color: '#fff',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    content: {
      padding: theme.spacing(0),
      '&:first-child': {
        paddingTop: 0,
      },
    },
    section: {
      padding: theme.spacing(2.5, 3.5),
      '&:not(:last-child)': {
        borderBottom: '1px solid #E3EBF6',
      },
    },
    sectionDescription: {
      backgroundColor: '#FAFCFF',
    },
    sectionCauses: {
      backgroundColor: '#fff',
    },
    sectionSteps: {
      backgroundColor: '#F5FAF7',
    },
    sectionCallout: {
      backgroundColor: '#FFFBF0',
    },
    sectionTitle: {
      fontWeight: 700,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      color: '#546E7A',
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '&::after': {
        content: '""',
        flex: 1,
        height: 1,
        background: '#E0E8F0',
      },
    },
    descriptionText: {
      fontSize: '0.875rem',
      lineHeight: 1.75,
      color: '#37474F',
    },
    causeItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#455A64',
    },
    causeDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: '#90A4AE',
      flexShrink: 0,
      marginTop: 8,
    },
    stepItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
      fontSize: '0.875rem',
      lineHeight: 1.7,
      color: '#37474F',
    },
    stepNumber: {
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: '#E8F5E9',
      color: '#2E7D32',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.7rem',
      fontWeight: 700,
      flexShrink: 0,
      marginTop: 1,
    },
    callout: {
      background: '#FFFBF0',
      border: '1px solid #FFE082',
      borderLeft: '4px solid #FFA000',
      borderRadius: 6,
      padding: theme.spacing(2, 2.5),
      fontSize: '0.84rem',
      lineHeight: 1.7,
      color: '#5D4037',
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
    },
    calloutIcon: {
      color: '#F57F17',
      fontSize: 20,
      flexShrink: 0,
      marginTop: 2,
    },
  }),
);

export function findMatchingGuide(errorText: string): ErrorGuideEntry | null {
  if (!errorText) return null;
  const lower = errorText.toLowerCase();
  return (
    (errorGuideData as ErrorGuideEntry[]).find((entry) => {
      try {
        return new RegExp(entry.pattern, 'i').test(lower);
      } catch {
        return lower.includes(entry.pattern.toLowerCase());
      }
    }) ?? null
  );
}

type ErrorGuideButtonProps = {
  errorText: string;
};

export function ErrorGuideButton({ errorText }: ErrorGuideButtonProps) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const guide = findMatchingGuide(errorText);

  if (!guide) return null;

  return (
    <>
      <IconButton
        className={classes.helpIcon}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="View error resolution guide">
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
      <ErrorGuideDialog
        open={open}
        onClose={() => setOpen(false)}
        guide={guide}
      />
    </>
  );
}

type ErrorGuideDialogProps = {
  open: boolean;
  onClose: () => void;
  guide: ErrorGuideEntry;
};

function ErrorGuideDialog({ open, onClose, guide }: ErrorGuideDialogProps) {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}>
      <IconButton className={classes.closeButton} onClick={onClose} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box className={classes.header}>
        <LocalHospitalIcon className={classes.headerIcon} />
        <Typography className={classes.headerSubtitle}>
          Error Resolution Guide
        </Typography>
        <Typography className={classes.headerTitle}>
          {guide.name}
        </Typography>
      </Box>

      <DialogContent className={classes.content}>
        <div className={`${classes.section} ${classes.sectionDescription}`}>
          <Typography className={classes.sectionTitle}>
            What it means
          </Typography>
          <Typography className={classes.descriptionText}>
            {guide.description}
          </Typography>
          {guide.descriptionExtra && (
            <Typography className={classes.descriptionText} style={{ marginTop: 10 }}>
              {guide.descriptionExtra}
            </Typography>
          )}
        </div>

        {guide.callout && (
          <div className={`${classes.section} ${classes.sectionCallout}`}>
            <div className={classes.callout}>
              <WarningIcon className={classes.calloutIcon} />
              <span>{guide.callout}</span>
            </div>
          </div>
        )}

        <div className={`${classes.section} ${classes.sectionCauses}`}>
          <Typography className={classes.sectionTitle}>
            Likely causes
          </Typography>
          {guide.causes.map((cause, i) => (
            <div key={i} className={classes.causeItem}>
              <div className={classes.causeDot} />
              <span>{cause}</span>
            </div>
          ))}
        </div>

        <div className={`${classes.section} ${classes.sectionSteps}`}>
          <Typography className={classes.sectionTitle}>
            Resolution steps
          </Typography>
          {guide.steps.map((step, i) => (
            <div key={i} className={classes.stepItem}>
              <div className={classes.stepNumber}>{i + 1}</div>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ErrorGuideDialog;
